import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const navTextClass = scrolled
    ? 'text-foreground-800 hover:text-foreground-950'
    : 'text-white/90 hover:text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)]';

  const btnOutlineClass = scrolled
    ? 'border-foreground-300 text-foreground-700 hover:bg-foreground-950 hover:text-white hover:border-foreground-950'
    : 'border-white/60 text-white hover:bg-white hover:text-foreground-950';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-gradient-to-b from-black/45 via-black/25 to-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 flex items-center justify-between h-16">
        <Link
          to="/"
          className="flex items-center gap-2 cursor-pointer"
        >
          <span className={`text-lg md:text-xl font-bold font-heading tracking-tight whitespace-nowrap ${scrolled ? 'text-foreground-950' : 'text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]'}`}>
            Hoobuy <span className="font-light italic">Sheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className={`text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${navTextClass}`}>
            Início
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1 ${navTextClass}`}
            >
              Categorias
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 bg-background-50 rounded-xl border border-background-200/60 py-2 w-60 max-h-80 overflow-y-auto"
                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
              >
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <span className="w-6 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-foreground-400 text-sm`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link to="/guia-de-compras" className={`text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${navTextClass}`}>
            Guia de Compras
          </Link>

          <button
            onClick={() => scrollToSection('seguranca')}
            className={`text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${navTextClass}`}
          >
            Segurança
          </button>

          <button
            onClick={() => scrollToSection('faq')}
            className={`text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${navTextClass}`}
          >
            FAQ
          </button>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className={`px-4 py-2 rounded-full text-sm font-semibold border cursor-pointer whitespace-nowrap transition-all ${btnOutlineClass}`}
          >
            Hoobuy Sheet
          </a>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg ${scrolled ? 'text-foreground-950 hover:bg-background-100' : 'text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)] hover:bg-white/10'}`}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-5 py-5 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 text-sm font-semibold text-foreground-800 py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-home-4-line text-foreground-400"></i>
            Início
          </Link>

          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="flex items-center justify-between w-full text-sm font-semibold text-foreground-800 py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <span className="flex items-center gap-3">
              <i className="ri-grid-line text-foreground-400"></i>
              Categorias
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {mobileCatOpen && (
            <div className="ml-4 pl-4 border-l-2 border-background-200/60 grid grid-cols-2 gap-1 mb-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 text-xs text-foreground-600 hover:text-primary-600 py-2 px-1.5 rounded hover:bg-background-100 cursor-pointer whitespace-nowrap transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <i className={`${cat.icon} text-foreground-400 text-xs`}></i>
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <Link to="/guia-de-compras" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 text-sm font-semibold text-foreground-800 py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-book-open-line text-foreground-400"></i>
            Guia de Compras
          </Link>

          <button onClick={() => scrollToSection('seguranca')} className="flex items-center gap-3 text-sm font-semibold text-foreground-800 py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer text-left">
            <i className="ri-shield-check-line text-foreground-400"></i>
            Segurança
          </button>

          <button onClick={() => scrollToSection('faq')} className="flex items-center gap-3 text-sm font-semibold text-foreground-800 py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer text-left">
            <i className="ri-question-line text-foreground-400"></i>
            FAQ
          </button>

          <div className="mt-3 pt-4 border-t border-background-200/60 flex flex-col gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full text-center px-4 py-3 rounded-full text-sm font-semibold border border-foreground-300 text-foreground-700 hover:bg-foreground-950 hover:text-white hover:border-foreground-950 cursor-pointer whitespace-nowrap transition-all"
            >
              Hoobuy Sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}